'use client';

import { useQuery } from '@tanstack/react-query';

/** Fields of a blog event consumed by the Kajian dropdown preview card. */
export interface FeaturedEvent {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImage: string | null;
  /** ISO start time, used to prefill the Google Calendar reminder link. */
  startAt: string;
  endAt: string | null;
  /** IANA timezone the event is scheduled in, e.g. "Asia/Jakarta". */
  timezone: string;
  /** True when this is the admin-highlighted event, false for the upcoming fallback. */
  isHighlighted: boolean;
}

/** Response envelope of the public events listing (/api/blog/events). */
interface EventsResponse {
  data: FeaturedEvent[];
}

/**
 * Loads the promoted event for the Kajian dropdown preview: the
 * admin-highlighted event when one exists, otherwise the nearest upcoming
 * event (the API performs the fallback).
 *
 * The request stays disabled until the dropdown opens so the events API is
 * not hit on every page load. Once fetched, the result is cached for a few
 * minutes so reopening the dropdown is instant.
 */
export function useFeaturedEvent(enabled: boolean) {
  return useQuery<EventsResponse>({
    queryKey: ['blog-events', 'featured'],
    queryFn: async () => {
      const res = await fetch('/api/blog/events?highlighted=1');
      if (!res.ok) throw new Error('Failed to fetch featured event');
      return res.json();
    },
    enabled,
    staleTime: 1000 * 60 * 5,
    retry: 3,
  });
}
